import { useCallback, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useStyles } from "./MillSettingsStep.styles";
import { IMillSettingsStep, IMillSettingsStepFormValues, IShift } from "./MillSettingsStepInterfaces";
import { millSettingsStepFormSchema } from "validations/schemas";
import { LanguagesEntity } from "entities/Languages/LanguagesEntity";
import { MillsEntity } from "entities/Mills/MillsEntity";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import CrewInput from "./CrewInput";
import ShiftsInput from "./ShiftsInput";
import KeySuccessFactorsInput from "./KeySuccessFactorsInput";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { removeCorporateOnboarding } from "utilities/storageUtilities";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CORPORATE_BACKOFFICE_PATHS, DefaultTranslationFieldsInTable } from "constants/constants";
import { areShiftDaysIdsEqual } from "utilities/utilities";
import { IShiftFromMill } from "./MillSettingsStepAPIInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";

dayjs.extend(customParseFormat);

const MillSettingsStep = ({ mill, onFinish, onBackStep, onlyMillSettings }: IMillSettingsStep) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [editCrewField, setEditCrewField] = useState<number | null>(null);
    const [editCustomKeySuccessFactorsField, setEditCustomKeySuccessFactorsField] = useState<number | null>(null);
    const [editShiftsField, setEditShiftsField] = useState<number | null>(null);
    const [searchParams] = useSearchParams();
    const millId = searchParams.get("millId");
    const [overlappingShifts, setOverlappingShifts] = useState<boolean>(false);
    const { refetch: refetchMillTimezone } = MillsEntity.GetMillTimezone(mill.mill_id);

    const timezones = moment.tz.names();

    const classes = useStyles();

    const { loading: isLoadingLanguages, languages } = LanguagesEntity.GetLanguages();
    const { upsertMillShiftsKSFCrewsDataSettings, error: apolloMutationError } = MillsEntity.UpdateMillSettings();
    const {
        loading: isLoadingData,
        dayArray,
        measureMetricArray,
        keySuccessFactorsArray,
    } = MillsEntity.GetMillSettingsData();

    const enabledKeySuccessFactors = useMemo(
        () =>
            mill.mill_key_success_factors
                .filter((millKeySuccessFactor) => millKeySuccessFactor.is_enabled)
                .map((enabledKeySuccessFactor) => enabledKeySuccessFactor.key_success_factor.key_success_factors_id),
        [mill],
    );

    const customKeySuccessFactors = useMemo(
        () =>
            keySuccessFactorsArray
                .filter((keySuccessFactor) => keySuccessFactor?.corporate_id)
                .map((keySuccessFactor) => ({
                    ...keySuccessFactor,
                    enabled: enabledKeySuccessFactors.includes(keySuccessFactor.key_success_factors_id),
                })),
        [keySuccessFactorsArray, enabledKeySuccessFactors],
    );

    const isCustomKeySuccessFactors = useMemo(
        () => keySuccessFactorsArray.filter((keySuccessFactor) => keySuccessFactor?.corporate_id).length > 0,
        [keySuccessFactorsArray],
    );

    const shiftsFormatted = useMemo(
        () =>
            mill.shifts.map((shift) => ({
                shift_id: shift.shift_id,
                name: shift.name,
                shift_historic: {
                    shift_id: shift.shift_id,
                    start: shift.shift_historic[0]?.start_time,
                    end: shift.shift_historic[0]?.end_time,
                    days: shift.shift_historic[0]?.shift_days?.filter((day) => day.is_enabled).map((day) => day.day_id),
                    effective_at: shift.shift_historic[0]?.effective_at,
                },
                production_lines: shift.production_line_shifts?.map((productionLine) => {
                    return {
                        production_line_shift_id: productionLine.production_line_shift_id,
                        production_line_id: productionLine.production_line?.production_line_id,
                        name: productionLine.production_line?.name,
                    };
                }),
                production_line_shifts_changes: [],
            })),
        [mill],
    );

    const initFormValues: IMillSettingsStepFormValues = useMemo(
        () => ({
            languageId: mill.mill_data?.language_id || "",
            firstDayOfReportWeekId: mill.mill_data?.first_day_reporting_week || "",
            measureMetricId: mill.mill_data?.measure_metric_id || "",
            timezone: mill.mill_data?.timezone || "",
            crewInput: "",
            crewEditInput: "",
            crewList: mill.crews || [],
            keySuccessFactorsIds: enabledKeySuccessFactors || [],
            isCustomKeySuccessFactors: isCustomKeySuccessFactors || false,
            customKeySuccessFactorsInput: "",
            customKeySuccessFactorsEditInput: "",
            customKeySuccessFactorsList: customKeySuccessFactors || [],
            shiftInput: "",
            shiftEditInput: "",
            allShiftsSoftDeleted: false,
            shifts: shiftsFormatted || [],
            firstDayFiscalYear: mill?.mill_data?.first_day_fiscal_year || moment(new Date()).format("YYYY-MM-DD"),
        }),
        [mill, enabledKeySuccessFactors, customKeySuccessFactors, isCustomKeySuccessFactors, shiftsFormatted],
    );

    const languagesOptions = useMemo(
        () => languages.map((language) => ({ code: language.language_id, name: language.name })),
        [languages],
    );

    const dayOptions = useMemo(
        () =>
            dayArray.map((day) => {
                const defaultDayTranslatedName = translateDefaultEntity(
                    i18n.language,
                    day,
                    "name",
                    DefaultTranslationFieldsInTable.DAY,
                );
                return { code: day.day_id, name: defaultDayTranslatedName };
            }),
        [dayArray, i18n.language],
    );

    const measureMetricOptions = useMemo(
        () =>
            measureMetricArray.map((metric) => {
                const defaultMeasureMetricTranslatedName = translateDefaultEntity(
                    i18n.language,
                    metric,
                    "name",
                    DefaultTranslationFieldsInTable.MEASURE_METRIC,
                );
                return { code: metric.measure_metric_id, name: defaultMeasureMetricTranslatedName };
            }),
        [i18n.language, measureMetricArray],
    );

    const defaultKeySuccessFactorsArrayOnly = useMemo(
        () => keySuccessFactorsArray.filter((keySuccessFactor) => !keySuccessFactor.corporate_id),
        [keySuccessFactorsArray],
    );

    const areShiftsDifferent = useCallback((shift1: IShiftFromMill, shift2: IShift) => {
        return (
            shift1.shift_historic[0]?.start_time !== shift2.shift_historic.start ||
            shift1.shift_historic[0]?.end_time !== shift2.shift_historic.end ||
            shift2?.production_line_shifts_changes?.length ||
            shift2?.disabled_at ||
            shift2?.deleted_at ||
            shift1.name !== shift2.name ||
            !areShiftDaysIdsEqual(
                shift1.shift_historic[0]?.shift_days
                    .filter((shift_day) => shift_day.is_enabled)
                    .map((shiftDay) => shiftDay.day_id)
                    .sort(),
                shift2.shift_historic.days.sort(),
            )
        );
    }, []);

    const productionLinesShifts = useMemo(
        () =>
            mill.production_lines.map((productionLine) => productionLine.production_line_shifts.map((shift) => shift)),
        [mill.production_lines],
    );

    const handleSubmit = useCallback(
        async (values: IMillSettingsStepFormValues, { setSubmitting }: FormikHelpers<IMillSettingsStepFormValues>) => {
            if (overlappingShifts) {
                setSubmitting(false);
                return;
            }

            const editedShifts = values.shifts.filter(
                (editedShift) =>
                    !editedShift.shift_id ||
                    mill.shifts.some((originalShift) => {
                        return (
                            originalShift.shift_id === editedShift.shift_id &&
                            areShiftsDifferent(originalShift, editedShift)
                        );
                    }),
            );

            const shiftsValues = editedShifts.map((shift) => {
                const productionLineassignmentChanges = shift?.production_line_shifts_changes?.map(
                    ({ name, ...plShiftChanges }) => plShiftChanges,
                );

                if (shift?.shift_id) {
                    const existingShiftsIndex = mill?.shifts?.findIndex(
                        (existingShift) => existingShift.shift_id === shift?.shift_id,
                    );

                    //Editing existing shift or soft deleting
                    return {
                        mill_id: mill.mill_id,
                        shift_id: shift?.shift_id,
                        ...(shift?.deleted_at && { deleted_at: shift?.deleted_at }),
                        ...(shift?.disabled_at && { disabled_at: shift?.disabled_at }),
                        name: shift.name,
                        start_time: shift.shift_historic.start,
                        end_time: shift.shift_historic.end,
                        ...(productionLineassignmentChanges?.length && {
                            production_line_shifts: {
                                data: productionLineassignmentChanges,
                                on_conflict: {
                                    constraint: "production_line_shift_pkey",
                                    update_columns: ["disabled_at"],
                                },
                            },
                        }),
                        shift_historic: {
                            data: [
                                {
                                    start_time: shift.shift_historic.start,
                                    end_time: shift.shift_historic.end,
                                    effective_at: shift.shift_historic.future_effective_at
                                        ? shift.shift_historic.future_effective_at
                                        : shift.shift_historic.effective_at,
                                    shift_days: {
                                        data: [
                                            //Existing shifts day array for edition
                                            ...mill?.shifts[existingShiftsIndex]?.shift_historic[0]?.shift_days?.map(
                                                (shift_day) => {
                                                    if (shift.shift_historic.days.includes(shift_day.day_id))
                                                        return {
                                                            day_id: shift_day.day_id,
                                                            is_enabled: true,
                                                        };
                                                    else
                                                        return {
                                                            day_id: shift_day.day_id,
                                                            is_enabled: false,
                                                        };
                                                },
                                            ),
                                            //Creation of new shift days array
                                            ...shift.shift_historic.days
                                                .filter((day) => {
                                                    const newDay = !mill?.shifts[
                                                        existingShiftsIndex
                                                    ]?.shift_historic[0]?.shift_days?.filter(
                                                        (shift_day) => shift_day.day_id === day,
                                                    ).length;
                                                    return newDay;
                                                })
                                                .map((day) => ({
                                                    day_id: day,
                                                    is_enabled: true,
                                                })),
                                        ],
                                        on_conflict: {
                                            constraint: "shift_day_pkey",
                                            update_columns: ["is_enabled"],
                                        },
                                    },
                                },
                            ],
                        },
                    };
                } else {
                    //Creating new shift
                    return {
                        mill_id: mill.mill_id,
                        name: shift.name,
                        start_time: shift.shift_historic.start,
                        end_time: shift.shift_historic.end,
                        ...(productionLineassignmentChanges?.length && {
                            production_line_shifts: {
                                data: productionLineassignmentChanges,
                                on_conflict: {
                                    constraint: "production_line_shift_pkey",
                                    update_columns: ["disabled_at"],
                                },
                            },
                        }),
                        shift_historic: {
                            data: [
                                {
                                    start_time: shift.shift_historic.start,
                                    end_time: shift.shift_historic.end,
                                    effective_at: shift.shift_historic.future_effective_at,
                                    shift_days: {
                                        data: shift.shift_historic.days.map((day) => ({
                                            day_id: day,
                                            is_enabled: true,
                                        })),
                                        on_conflict: {
                                            constraint: "shift_day_pkey",
                                            update_columns: ["day_id", "is_enabled"],
                                        },
                                    },
                                },
                            ],
                            on_conflict: {
                                constraint: "shift_historic_pkey",
                                update_columns: ["shift_id", "start_time", "end_time", "effective_at"],
                            },
                        },

                        created_at: shift.created_at,
                    };
                }
            });

            //Creating, editing or soft deleting crews
            const crewsValues = values.crewList.map((crew) => ({
                ...(crew?.crew_id && { crew_id: crew?.crew_id }),
                ...(crew?.deleted_at && { deleted_at: crew?.deleted_at }),
                name: crew.name,
                mill_id: mill.mill_id,
            }));

            //Creation or edition of mill data
            const millDataValues = {
                ...(mill?.mill_data?.mill_data_id && { mill_data_id: mill?.mill_data?.mill_data_id }),
                language_id: values.languageId,
                mill_id: mill.mill_id,
                measure_metric_id: values.measureMetricId,
                timezone: values.timezone,
                first_day_reporting_week: values.firstDayOfReportWeekId,
                first_day_fiscal_year: values.firstDayFiscalYear,
            };

            const millKeySuccessFactorsValues = [
                //Array with new custom key success factors not registered, with possible soft deletion of custom key success factors
                ...values.customKeySuccessFactorsList
                    .filter((customFactor) => {
                        const unRegisteredCheckedKeySucessFactor =
                            mill.mill_key_success_factors.findIndex(
                                (millKeySuccessFactor) =>
                                    customFactor?.key_success_factors_id ===
                                    millKeySuccessFactor.key_success_factor.key_success_factors_id,
                            ) === -1;
                        return (
                            !customFactor?.key_success_factors_id ||
                            (customFactor?.key_success_factors_id && unRegisteredCheckedKeySucessFactor)
                        );
                    })
                    .map((customFactor) => ({
                        is_enabled: customFactor.enabled,
                        mill_id: mill.mill_id,
                        key_success_factor: {
                            data: {
                                ...(customFactor?.key_success_factors_id && {
                                    key_success_factors_id: customFactor?.key_success_factors_id,
                                }),
                                corporate_id: mill.corporate_id,
                                name: customFactor.name,
                                ...(customFactor?.deleted_at && {
                                    deleted_at: customFactor?.deleted_at,
                                }),
                            },
                            on_conflict: {
                                constraint: "key_success_factors_pkey",
                                update_columns: ["name", "deleted_at"],
                            },
                        },
                    })),
                //Array with existing custom key success factors selections that were checked or unchecked edition, with possible
                //soft deletion
                ...mill?.mill_key_success_factors.map((millKeySuccessFactors) => {
                    const customSucessFactorIndex = values.customKeySuccessFactorsList.findIndex(
                        (customFactor) =>
                            customFactor?.key_success_factors_id ===
                            millKeySuccessFactors.key_success_factor.key_success_factors_id,
                    );

                    return {
                        mill_key_success_factors_id: millKeySuccessFactors.mill_key_success_factors_id,
                        is_enabled: values.keySuccessFactorsIds.includes(
                            millKeySuccessFactors.key_success_factor.key_success_factors_id,
                        ),
                        mill_id: mill.mill_id,
                        ...(customSucessFactorIndex === -1 && {
                            key_success_factors_id: millKeySuccessFactors.key_success_factor.key_success_factors_id,
                        }),
                        ...(customSucessFactorIndex !== -1 && {
                            key_success_factor: {
                                data: {
                                    name: values.customKeySuccessFactorsList[customSucessFactorIndex].name,
                                    key_success_factors_id:
                                        values.customKeySuccessFactorsList[customSucessFactorIndex]
                                            .key_success_factors_id,
                                    ...(values.customKeySuccessFactorsList[customSucessFactorIndex]?.deleted_at && {
                                        deleted_at:
                                            values.customKeySuccessFactorsList[customSucessFactorIndex].deleted_at,
                                    }),
                                },
                                on_conflict: {
                                    constraint: "key_success_factors_pkey",
                                    update_columns: ["name", "deleted_at"],
                                },
                            },
                        }),
                    };
                }),
                //Array with new checked registry of default key success factors
                ...values.keySuccessFactorsIds
                    .filter((keySuccessFactorId) => {
                        const unRegisteredCheckedKeySucessFactor =
                            mill?.mill_key_success_factors.findIndex(
                                (millKeySuccessFactor) =>
                                    keySuccessFactorId ===
                                    millKeySuccessFactor?.key_success_factor.key_success_factors_id,
                            ) === -1;
                        const notCustomKeySuccessFactor =
                            values.customKeySuccessFactorsList.findIndex(
                                (customFactor) => customFactor?.key_success_factors_id === keySuccessFactorId,
                            ) === -1;

                        return notCustomKeySuccessFactor && unRegisteredCheckedKeySucessFactor;
                    })
                    .map((id) => {
                        const defaultkeySuccessFactorIndex = defaultKeySuccessFactorsArrayOnly.findIndex(
                            (defaultKSF) => defaultKSF?.key_success_factors_id === id,
                        );
                        return {
                            is_enabled: true,
                            mill_id: mill.mill_id,
                            key_success_factors_id:
                                defaultKeySuccessFactorsArrayOnly[defaultkeySuccessFactorIndex]?.key_success_factors_id,
                        };
                    }),
            ];

            try {
                const response = await upsertMillShiftsKSFCrewsDataSettings({
                    variables: {
                        shifts: shiftsValues,
                        crews: crewsValues,
                        mill_key_success_factors: millKeySuccessFactorsValues,
                        mill_data: millDataValues,
                    },
                });
                if (response) {
                    removeCorporateOnboarding();
                    await onFinish();
                    refetchMillTimezone();
                }
            } catch {
                return;
            }
        },
        [
            mill,
            defaultKeySuccessFactorsArrayOnly,
            upsertMillShiftsKSFCrewsDataSettings,
            onFinish,
            areShiftsDifferent,
            overlappingShifts,
            refetchMillTimezone,
        ],
    );

    if (isLoadingData || isLoadingLanguages)
        return (
            <div className={classes.marginTop}>
                <div>{t("general_information.loading_corporate_and_mill_data")}</div>
                <Box className={classes.loadingContainer}>
                    <CircularProgress color="info" />
                </Box>
            </div>
        );

    return (
        <div>
            <div className={classes.titleContainer}>
                {(!onlyMillSettings || Boolean(millId)) && (
                    <IconButton
                        onClick={() =>
                            Boolean(millId)
                                ? navigate(
                                      `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${millId}`,
                                  )
                                : onBackStep()
                        }
                        className={classes.backButton}
                    >
                        <ArrowBackIosIcon />
                    </IconButton>
                )}
                <h1 className={classes.formTitle}>{t("mill_setup.title")}</h1>
            </div>
            <Formik
                initialValues={initFormValues}
                validationSchema={millSettingsStepFormSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, values, handleChange, setFieldValue, dirty }) => {
                    const formError = Object.keys(errors).length > 0 && Object.keys(touched).length > 0;
                    return (
                        <Form className={classes.fieldsContainer}>
                            <Snackbar
                                open={dirty}
                                anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                }}
                            >
                                <Alert
                                    severity={"info"}
                                    sx={{
                                        width: "100%",
                                        marginTop: "50px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {t("mill_setup.unsaved_changes_message")}
                                </Alert>
                            </Snackbar>
                            <div className={classes.formSubtitle}>{t("mill_setup.general_settings")}</div>

                            <FormControl
                                className={classes.textField}
                                fullWidth
                                error={!!errors.languageId && !!touched.languageId}
                            >
                                <InputLabel id="languageId-select-label">{t("mill_setup.language")}</InputLabel>
                                {isLoadingLanguages ? (
                                    <Box sx={{ display: "flex" }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        <Select
                                            labelId="languageId-select-label"
                                            name="languageId"
                                            value={values.languageId}
                                            label={t("mill_setup.language")}
                                            onChange={handleChange}
                                            error={!!errors.languageId && !!touched.languageId}
                                        >
                                            {languagesOptions.map((language) => (
                                                <MenuItem key={language.code} value={language.code}>
                                                    {language.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {!!errors.languageId && !!touched.languageId && (
                                            <FormHelperText>{t(errors.languageId)}</FormHelperText>
                                        )}
                                    </>
                                )}
                            </FormControl>

                            <FormControl
                                className={classes.textField}
                                fullWidth
                                error={!!errors.firstDayOfReportWeekId && !!touched.firstDayOfReportWeekId}
                            >
                                <InputLabel id="firstDateReporting-select-label">
                                    {t("mill_setup.first_day_of_reporting_week")}
                                </InputLabel>
                                {isLoadingData ? (
                                    <Box sx={{ display: "flex" }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        <Select
                                            labelId="firstDateReporting-select-label"
                                            name="firstDayOfReportWeekId"
                                            value={values.firstDayOfReportWeekId}
                                            label="First Day of Reporting Week"
                                            onChange={handleChange}
                                        >
                                            {dayOptions.map((day) => (
                                                <MenuItem key={day.code} value={day.code}>
                                                    {day.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {!!errors.firstDayOfReportWeekId && !!touched.firstDayOfReportWeekId && (
                                            <FormHelperText>{t(errors.firstDayOfReportWeekId)}</FormHelperText>
                                        )}
                                    </>
                                )}
                            </FormControl>

                            <FormControl
                                className={classes.textField}
                                fullWidth
                                error={!!errors.measureMetricId && !!touched.measureMetricId}
                            >
                                <InputLabel id="metrics-select-label">{t("mill_setup.metrics")}</InputLabel>
                                {isLoadingData ? (
                                    <Box sx={{ display: "flex" }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        <Select
                                            labelId="metrics-select-label"
                                            name="measureMetricId"
                                            value={values.measureMetricId}
                                            label={t("mill_setup.metrics")}
                                            onChange={handleChange}
                                        >
                                            {measureMetricOptions.map((measureMetric) => (
                                                <MenuItem key={measureMetric.code} value={measureMetric.code}>
                                                    {measureMetric.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {!!errors.measureMetricId && !!touched.measureMetricId && (
                                            <FormHelperText>{t(errors.measureMetricId)}</FormHelperText>
                                        )}
                                    </>
                                )}
                            </FormControl>

                            <FormControl
                                className={classes.textField}
                                fullWidth
                                error={!!errors.timezone && !!touched.timezone}
                            >
                                <InputLabel id="timezone-select-label">{t("mill_setup.time_zone")}</InputLabel>
                                {isLoadingData ? (
                                    <Box sx={{ display: "flex" }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        <Select
                                            labelId="timezone-select-label"
                                            name="timezone"
                                            value={values.timezone}
                                            label={t("mill_setup.time_zone")}
                                            onChange={handleChange}
                                        >
                                            {timezones.map((timezone) => (
                                                <MenuItem key={timezone} value={timezone}>
                                                    {timezone}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {!!errors.timezone && !!touched.timezone && (
                                            <FormHelperText>{t(errors.timezone)}</FormHelperText>
                                        )}
                                    </>
                                )}
                            </FormControl>

                            <FormControl className={classes.textField} fullWidth>
                                <DatePicker
                                    value={dayjs(values.firstDayFiscalYear)}
                                    slotProps={{
                                        textField: {
                                            error: !!errors.firstDayFiscalYear && !!touched.firstDayFiscalYear,
                                            helperText:
                                                !!errors.firstDayFiscalYear &&
                                                !!touched.firstDayFiscalYear &&
                                                t(errors.firstDayFiscalYear),
                                        },
                                    }}
                                    label={t("mill_setup.first_day_fiscal_year")}
                                    format="MM/DD"
                                    onChange={(date) => {
                                        setFieldValue(
                                            "firstDayFiscalYear",
                                            moment(new Date(`${date}`)).format("YYYY-MM-DD") === "Invalid date"
                                                ? ""
                                                : moment(new Date(`${date}`)).format("YYYY-MM-DD"),
                                        );
                                    }}
                                />
                            </FormControl>

                            <CrewInput
                                handleChange={handleChange}
                                setFieldValue={setFieldValue}
                                values={values}
                                editCrewField={editCrewField}
                                setEditCrewField={setEditCrewField}
                            />

                            <ShiftsInput
                                handleChange={handleChange}
                                setFieldValue={setFieldValue}
                                values={values}
                                errors={errors}
                                touched={touched}
                                dayArray={dayArray}
                                editShiftsField={editShiftsField}
                                setEditShiftsField={setEditShiftsField}
                                productionLinesShifts={productionLinesShifts}
                                setOverlappingShifts={setOverlappingShifts}
                            />

                            <KeySuccessFactorsInput
                                handleChange={handleChange}
                                values={values}
                                keySuccessFactorsArray={defaultKeySuccessFactorsArrayOnly}
                                setFieldValue={setFieldValue}
                                editCustomKeySuccessFactorsField={editCustomKeySuccessFactorsField}
                                setEditCustomKeySuccessFactorsField={setEditCustomKeySuccessFactorsField}
                            />

                            <p
                                style={{
                                    display: apolloMutationError || formError ? "block" : "none",
                                    color: "red",
                                    width: "100%",
                                }}
                            >
                                {apolloMutationError?.message.includes("uq_crew_name")
                                    ? t("mill_setup.error_message.existing_crew_name")
                                    : apolloMutationError?.message.includes("uq_shift_name")
                                    ? t("mill_setup.error_message.existing_shift_name")
                                    : apolloMutationError?.message.includes("uq_corporate_key_success_factors_name")
                                    ? t("mill_setup.error_message.existing_key_success_factor_name")
                                    : formError
                                    ? t("field_errors.required_field_missing")
                                    : t("setup_corporation.errors.creation_error")}
                            </p>

                            <div className={classes.titleContainer}>
                                <Button
                                    className={classes.submitButton}
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {onlyMillSettings
                                        ? t("mill_setup.action_only_mill_settings")
                                        : t("mill_setup.action")}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default MillSettingsStep;
